import React, { useState, useEffect } from "react";
import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { getClientes } from "../../../services/modules/Clientes";
import { getPapeleria } from "../../../services/modules/Papeleria";

const PapeleriaVigencia = () => {
  const [clientes, setClientes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOption, setFilterOption] = useState("all"); // Opciones: all, client, vigencia
  const [searchDPI, setSearchDPI] = useState(""); // Para buscar por DPI
  const [vigenciaFilter, setVigenciaFilter] = useState("vigente"); // Opciones: vigente, no_vigente, proximo_a_vencer

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const clientesData = await getClientes();
      const papeleriaData = await Promise.all(
        clientesData.map((cliente) => getPapeleria(cliente.id))
      );

      const combinedData = clientesData.map((cliente, index) => {
        const documentos = papeleriaData[index] || [];
        return { ...cliente, documentos };
      });

      setClientes(combinedData);
    } catch (err) {
      console.error("Error al cargar los datos:", err);
    } finally {
      setLoading(false);
    }
  };

  const calcularProgresoEstado = (fechaVencimiento) => {
    if (!fechaVencimiento) return { porcentaje: 100, color: "gray", estado: "Sin Fecha" };

    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diferenciaDias = (vencimiento - hoy) / (1000 * 60 * 60 * 24);

    if (diferenciaDias < 0) return { porcentaje: 100, color: "red", estado: "Vencido" };
    if (diferenciaDias <= 14) return { porcentaje: 50, color: "orange", estado: "Próximo a vencer" };
    if (diferenciaDias <= 30) return { porcentaje: 50, color: "yellow", estado: "Vigente" };
    return { porcentaje: 100, color: "green", estado: "Vigente" };
  };

  const handleFilter = () => {
    if (filterOption === "all") {
      setFilteredData(clientes);
    } else if (filterOption === "client") {
      const filtered = clientes.filter((cliente) => cliente.dpi === searchDPI);
      setFilteredData(filtered);
    } else if (filterOption === "vigencia") {
      const filtered = clientes
        .map((cliente) => {
          const documentos = cliente.documentos.filter((doc) => {
            const { estado } = calcularProgresoEstado(doc.FechaVencimiento);
            if (vigenciaFilter === "vigente") return estado === "Vigente";
            if (vigenciaFilter === "no_vigente") return estado === "Vencido";
            if (vigenciaFilter === "proximo_a_vencer") return estado === "Próximo a vencer";
            return false;
          });
          return { ...cliente, documentos };
        })
        .filter((cliente) => cliente.documentos.length > 0);
      setFilteredData(filtered);
    }
  };

  if (loading) return <p>Cargando...</p>;

  return (
    <div>
      <Typography variant="h4" align="center" gutterBottom>
        Vigencia de Papelería por Cliente
      </Typography>

      {/* Filtros */}
      <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
        <RadioGroup
          row
          value={filterOption}
          onChange={(e) => setFilterOption(e.target.value)}
        >
          <FormControlLabel value="all" control={<Radio />} label="Todos" />
          <FormControlLabel value="client" control={<Radio />} label="Por Cliente" />
          <FormControlLabel value="vigencia" control={<Radio />} label="Por Vigencia" />
        </RadioGroup>

        {filterOption === "client" && (
          <TextField
            label="DPI del Cliente"
            value={searchDPI}
            onChange={(e) => setSearchDPI(e.target.value)}
            variant="outlined"
            sx={{ mb: 2, width: "300px" }}
          />
        )}

        {filterOption === "vigencia" && (
          <RadioGroup
            row
            value={vigenciaFilter}
            onChange={(e) => setVigenciaFilter(e.target.value)}
          >
            <FormControlLabel value="vigente" control={<Radio />} label="Vigente" />
            <FormControlLabel value="no_vigente" control={<Radio />} label="No Vigente" />
            <FormControlLabel value="proximo_a_vencer" control={<Radio />} label="Próximo a vencer" />
          </RadioGroup>
        )}

        <Button variant="contained" color="primary" onClick={handleFilter}>
          Aplicar Filtro
        </Button>
      </Box>

      {/* Resultados */}
      {filteredData.map((cliente, index) => (
        <Box key={index} mb={4} p={2} border="1px solid #ddd" borderRadius="8px">
          <Typography variant="h6" sx={{ backgroundColor: "#4CAF50", color: "#fff", padding: "10px" }}>
            Documento de Identificación (DPI): {cliente.dpi} | Nombre: {cliente.nombres}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#004d99" }}>
                  <TableCell style={{ color: "#fff" }}>No.</TableCell>
                  <TableCell style={{ color: "#fff" }}>Nombre Documento</TableCell>
                  <TableCell style={{ color: "#fff" }}>Vigencia</TableCell>
                  <TableCell style={{ color: "#fff" }}>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cliente.documentos.map((doc, docIndex) => {
                  const { porcentaje, color, estado } = calcularProgresoEstado(doc.FechaVencimiento);
                  return (
                    <TableRow key={docIndex}>
                      <TableCell>{docIndex + 1}</TableCell>
                      <TableCell>{doc.NombreDocumento || "Sin Nombre"}</TableCell>
                      <TableCell>
                        {new Date(doc.FechaVencimiento).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap="10px">
                          <LinearProgress
                            variant="determinate"
                            value={porcentaje}
                            sx={{
                              width: "100px",
                              height: "8px",
                              backgroundColor: "lightgray",
                              "& .MuiLinearProgress-bar": { backgroundColor: color },
                            }}
                          />
                          <span>{estado}</span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </div>
  );
};

export default PapeleriaVigencia;
